import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  searchFellowshipApi,
  fetchFellowshipSentRequestsApi,
  fetchFellowshipReceivedRequestsApi,
  fetchFellowshipListApi,
  sendFellowshipRequestApi,
  acceptFellowshipRequestApi,
  declineFellowshipRequestApi,
  cancelFellowshipRequestApi,
  deleteFellowshipApi,
} from "../../services/network/fellowshipApi";
import { FellowshipStateType } from "../../types/network/fellowshipTypes";  // مسیر دقیق خودت
import { PayloadAction } from "@reduxjs/toolkit";
import { Draft } from "immer";

const initialState: FellowshipStateType = {
  searchResults: [],
  sentRequests: [],
  receivedRequests: [],
  fellowshipList: [],
  loading: false,
  error: null,
  successMessage: null,
};

interface SearchFellowshipArgs {
  query: string;
  page?: number;
}


// Thunks ------------------------------------------------------------------

// Search for users
export const searchFellowship = createAsyncThunk(
  "fellowship/searchFellowship",
  async ({ query, page = 1 }: SearchFellowshipArgs, { rejectWithValue }) => {
    try {
      const data = await searchFellowshipApi(query, page);
      return data; // Paginated response: includes .results, .count, .next, .previous
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error searching friends.");
    }
  }
);



// Fetch sent fellowship requests
export const fetchFellowshipSentRequests = createAsyncThunk(
  "fellowship/fetchSentRequests",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchFellowshipSentRequestsApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error fetching sent fellowship requests.");
    }
  }
);

// Fetch received fellowship requests
export const fetchFellowshipReceivedRequests = createAsyncThunk(
  "fellowship/fetchReceivedRequests",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchFellowshipReceivedRequestsApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error fetching received fellowship requests.");
    }
  }
);

// Fetch fellowship list
export const fetchFellowshipList = createAsyncThunk(
  "fellowship/fetchFellowshipList",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchFellowshipListApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error fetching fellowship list.");
    }
  }
);

// // Send a fellowship request
export const sendFellowshipRequest = createAsyncThunk<
  any,
  { toUserId: number; fellowshipType: string; reciprocalFellowshipType: string | null },
  { rejectValue: { error: string } }
>(
  "fellowship/sendRequest",
  async ({ toUserId, fellowshipType, reciprocalFellowshipType }, { rejectWithValue }) => {
    try {
      const data = await sendFellowshipRequestApi(toUserId, fellowshipType, reciprocalFellowshipType);
      return data;
    } catch (err: any) {
      return rejectWithValue({ error: err.response?.data?.error || "Error sending fellowship request." });
    }
  }
);

// Accept a fellowship request
export const acceptFellowshipRequest = createAsyncThunk(
  "fellowship/acceptRequest",
  async (
    { fellowshipId, reciprocalFellowshipType }: { fellowshipId: number; reciprocalFellowshipType: string | null },
    { rejectWithValue }
  ) => {
    try {
      const data = await acceptFellowshipRequestApi(fellowshipId, reciprocalFellowshipType);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error accepting fellowship request.");
    }
  }
);

// Decline a fellowship request
export const declineFellowshipRequest = createAsyncThunk(
  "fellowship/declineRequest",
  async (
    { fellowshipId, reciprocalFellowshipType }: { fellowshipId: number; reciprocalFellowshipType: string | null },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const data = await declineFellowshipRequestApi(fellowshipId, reciprocalFellowshipType);
      dispatch(fetchFellowshipReceivedRequests());
      return { message: data.message, id: fellowshipId };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error declining fellowship request.");
    }
  }
);


// Cancel a fellowship request
export const cancelFellowshipRequest = createAsyncThunk(
  "fellowship/cancelRequest",
  async (fellowshipId: number, { rejectWithValue, dispatch }) => {
    try {
      const data = await cancelFellowshipRequestApi(fellowshipId);

      dispatch(fetchFellowshipSentRequests());

      return { message: data.message, id: fellowshipId };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error canceling fellowship request.");
    }
  }
);

// Delete a fellowship
export const deleteFellowship = createAsyncThunk(
  "fellowship/deleteFellowship",
  async (
    { fellowshipId, relationshipType }: { fellowshipId: number; relationshipType: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await deleteFellowshipApi({ fellowshipId, relationshipType });
      return { fellowshipId, message: data.message }; // پیام و ID بازگردانده می‌شود
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Error deleting fellowship.");
    }
  }
);


// Slice ------------------------------------------------------------------
const fellowshipSlice = createSlice({
  name: "fellowship",
  initialState,
  reducers: {
    clearMessages(state: Draft<FellowshipStateType>) {
      state.error = null;
      state.successMessage = null;
    },
    clearSearchResults(state: Draft<FellowshipStateType>) {
      state.searchResults = [];
    },
    setError(state: Draft<FellowshipStateType>, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      // Search users
      .addCase(searchFellowship.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchFellowship.fulfilled, (state, action) => {
        state.loading = false;

        if (action.meta.arg.page === 1) {
          state.searchResults = action.payload.results;
        } else {
          state.searchResults = [...state.searchResults, ...action.payload.results];
        }
      })
      .addCase(searchFellowship.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch sent requests
      .addCase(fetchFellowshipSentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFellowshipSentRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.sentRequests = action.payload;
      })
      .addCase(fetchFellowshipSentRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch received requests
      .addCase(fetchFellowshipReceivedRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFellowshipReceivedRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.receivedRequests = action.payload;
      })
      .addCase(fetchFellowshipReceivedRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch fellowship list
      .addCase(fetchFellowshipList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFellowshipList.fulfilled, (state, action) => {
        state.loading = false;
        state.fellowshipList = action.payload;
      })
      .addCase(fetchFellowshipList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // // Send fellowship request
      .addCase(sendFellowshipRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendFellowshipRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || "Fellowship request sent successfully.";
        state.sentRequests.push(action.payload);
      })
      .addCase(sendFellowshipRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error as string;
      })

      // Accept fellowship request
      .addCase(acceptFellowshipRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptFellowshipRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || "Fellowship request accepted.";
      })
      .addCase(acceptFellowshipRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Decline fellowship request
      .addCase(declineFellowshipRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(declineFellowshipRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || "Fellowship request declined.";
        state.receivedRequests = state.receivedRequests.filter(
          (request) => request.id !== action.payload.id
        );
      })
      .addCase(declineFellowshipRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Cancel fellowship request
      .addCase(cancelFellowshipRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelFellowshipRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || "Fellowship request canceled.";
        state.sentRequests = state.sentRequests.filter(
          (request) => request.id !== action.payload.id
        );
      })
      .addCase(cancelFellowshipRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete fellowship
      .addCase(deleteFellowship.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFellowship.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || "Fellowship deleted successfully.";
      })
      .addCase(deleteFellowship.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearMessages, clearSearchResults, setError } = fellowshipSlice.actions;

export default fellowshipSlice.reducer;
